import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Stack,
  Textarea,
  Typography,
} from '@mui/joy';
import InsertDriveFileRoundedIcon from '@mui/icons-material/InsertDriveFileRounded';
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import OpenInNewRoundedIcon from '@mui/icons-material/OpenInNewRounded';
import DropZone from './DropZone';
import FileUpload from './FileUpload';
import { useFeedback } from './feedback/Service';
import { getCdnFileUrl } from '../utils';

const STATUS_CAPTION = {
  queued: "Queued for text extraction — you can keep working; we'll fill this in when it's ready.",
  running: 'Extracting text from PDF — you can keep working.',
  completed: 'Extracted from PDF — review and edit as needed.',
};

export const isParsePending = (status) => status === 'queued' || status === 'running';

// Returns an error message string when the job description is not in a saveable state,
// or null when it's OK. Encodes the single-source-of-truth contract: text required
// unless a PDF parse is still expected to fill it.
//
// `pdfChanged` (optional) — when true, the current pdfUrl is a fresh staged upload
// that hasn't been saved/parsed yet, so any cached completed/failed status is stale
// and a new parse is about to start. Treated as null status.
export const getDescriptionValidationError = ({ jobDescription, pdfUrl, jobDescriptionStatus, pdfChanged = false }) => {
  const trimmed = (jobDescription || '').trim();
  if (trimmed) return null;
  if (!pdfUrl) {
    return 'Please add a job description (upload a PDF or paste text).';
  }
  const effectiveStatus = pdfChanged ? null : jobDescriptionStatus;
  if (effectiveStatus === 'failed') {
    return 'PDF parsing failed — paste the description manually or upload a different file.';
  }
  if (effectiveStatus === 'completed') {
    return 'The description is empty. Re-extract from the PDF or paste text manually.';
  }
  return null;
};

const deriveCaption = ({ pdfUrl, jobDescription, jobDescriptionStatus, userEdited }) => {
  const hasText = (jobDescription || '').trim().length > 0;
  if (!pdfUrl && hasText) {
    return 'Using the description you typed. No file needed.';
  }
  if (jobDescriptionStatus === 'completed' && userEdited) {
    return 'Edited from the PDF extraction.';
  }
  return STATUS_CAPTION[jobDescriptionStatus] || null;
};

const JobDescriptionInput = ({
  pdfUrl,
  originalPdfUrl,
  jobDescription,
  jobDescriptionStatus,
  jobDescriptionError,
  uploadObject,
  onPdfChange,
  onPdfRemove,
  onTextChange,
  disabled = false,
  userEdited = false,
}) => {
  const feedback = useFeedback();

  const isUploading = !!uploadObject && uploadObject.progress < 100;
  const isCancelMode = isUploading || (!!pdfUrl && pdfUrl !== originalPdfUrl);
  // The cached status/error only applies when the *current* pdfUrl matches the one
  // the backend parsed (or is parsing). Hide it during a fresh-but-unsaved swap,
  // and hide it when the user has removed the PDF entirely — otherwise stale
  // "failed"/"completed" UI leaks across uploads or after removal.
  const statusApplies = !!pdfUrl && !isCancelMode;
  const effectiveStatus = statusApplies ? jobDescriptionStatus : null;
  const effectiveError = statusApplies ? jobDescriptionError : null;
  const isParsing = isParsePending(effectiveStatus);
  const willPdfOverwriteTypedText =
    !!pdfUrl &&
    userEdited &&
    (jobDescription || '').trim().length > 0 &&
    (effectiveStatus === 'queued' ||
      effectiveStatus === 'running' ||
      !effectiveStatus);
  const isEmptyAfterCompletedParse =
    !!pdfUrl &&
    effectiveStatus === 'completed' &&
    (jobDescription || '').trim().length === 0;

  const handleFileSelect = async (file) => {
    const hasText = !!(jobDescription && jobDescription.trim());
    const parseInFlight = isParsing; // already null in cancel mode
    let confirmOpts = null;
    if (parseInFlight && hasText) {
      confirmOpts = {
        title: 'Replace the PDF being processed?',
        description: 'A previous PDF is still being extracted, and the description text below will be replaced when the new extraction finishes.',
      };
    } else if (parseInFlight) {
      confirmOpts = {
        title: 'Replace the PDF being processed?',
        description: 'A previous PDF is still being extracted. Replace it with this new file? The extraction will be restarted.',
      };
    } else if (hasText) {
      confirmOpts = {
        title: 'Replace current description?',
        description: 'When we extract text from this PDF, it will replace the description below.',
      };
    }
    if (confirmOpts) {
      const confirmed = await feedback.confirm(confirmOpts);
      if (!confirmed) return;
    }
    onPdfChange(file);
  };

  const handleRemoveClick = async () => {
    if (isUploading) {
      uploadObject?.abort?.();
      // Parent still owns pdfUrl state; if the upload never landed, parent state is unchanged.
      // If it did land just before abort, fall through to rollback.
      if (pdfUrl && pdfUrl !== originalPdfUrl) onPdfRemove();
      return;
    }
    if (!isCancelMode && isParsing) {
      const confirmed = await feedback.confirm({
        title: 'Remove PDF while extraction is in progress?',
        description: 'The PDF will be removed. The current description text will be kept.',
      });
      if (!confirmed) return;
    }
    onPdfRemove();
  };

  const caption = deriveCaption({ pdfUrl, jobDescription, jobDescriptionStatus: effectiveStatus, userEdited });

  return (
    <Stack spacing={2}>
      <Typography level="body-sm" color="neutral">
        Upload a PDF to auto-extract the text, or paste it directly. The text below is what we use to evaluate candidates.
      </Typography>

      <Card variant="outlined">
        <CardContent>
          <Stack spacing={1.5}>
            <Box>
              <Typography level="title-md">Source document (optional)</Typography>
              <Typography level="body-sm" color="neutral">
                Upload a PDF or DOCX. We&apos;ll extract the text and fill the description below.
              </Typography>
            </Box>

            <DropZone
              onFileSelect={handleFileSelect}
              fileExists={!!pdfUrl}
              title="for this role"
            />

            {uploadObject && (
              <FileUpload
                icon={<InsertDriveFileRoundedIcon />}
                fileName={uploadObject.name}
                fileSize={uploadObject.size ? `${Math.round(uploadObject.size / 1024)} KB` : '0 KB'}
                progress={uploadObject.progress}
                onAbort={() => uploadObject.abort?.()}
              />
            )}

            {pdfUrl && (
              <Stack direction="row" spacing={1} flexWrap="wrap">
                <Button
                  variant="soft"
                  component="a"
                  href={getCdnFileUrl(pdfUrl)}
                  target="_blank"
                  rel="noopener noreferrer"
                  endDecorator={<OpenInNewRoundedIcon />}
                >
                  View PDF
                </Button>
                <Button
                  variant="plain"
                  color="danger"
                  onClick={handleRemoveClick}
                  disabled={disabled}
                  startDecorator={isCancelMode ? <CloseRoundedIcon /> : <DeleteOutlineRoundedIcon />}
                >
                  {isCancelMode ? 'Cancel upload' : 'Remove PDF'}
                </Button>
              </Stack>
            )}
          </Stack>
        </CardContent>
      </Card>

      <Card variant="outlined">
        <CardContent>
          <Stack spacing={1.5}>
            <Box>
              <Typography level="title-md">Description</Typography>
              <Typography level="body-sm" color="neutral">
                This is the canonical text used for evaluation. Edit anytime.
              </Typography>
            </Box>

            {willPdfOverwriteTypedText && (
              <Alert color="warning" variant="soft">
                Heads up: when PDF extraction finishes, the extracted text will replace what you typed. Remove the PDF to keep your text, or wait — once parsing finishes you can edit the extracted text instead.
              </Alert>
            )}

            {isEmptyAfterCompletedParse && (
              <Alert color="warning" variant="soft">
                The PDF text was extracted but the description is empty. Re-extract from the PDF, paste text manually, or remove the PDF before saving.
              </Alert>
            )}

            {effectiveStatus === 'failed' && (
              <Alert color="danger" variant="soft">
                PDF parsing failed{effectiveError ? `: ${effectiveError}` : '.'}{' '}
                Paste the text manually below, or upload a different file.
              </Alert>
            )}

            <Box sx={{ position: 'relative' }}>
              <Textarea
                value={jobDescription || ''}
                onChange={(e) => onTextChange(e.target.value)}
                minRows={14}
                placeholder="Paste the job description, requirements, responsibilities, success criteria, and must-have qualifications here."
                disabled={disabled}
                sx={{ '--Textarea-focusedThickness': '1px' }}
              />
              {isParsing && (
                <Box
                  sx={{
                    position: 'absolute',
                    top: 8,
                    right: 8,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.75,
                    px: 1,
                    py: 0.5,
                    borderRadius: 'sm',
                    bgcolor: 'background.surface',
                    boxShadow: 'sm',
                  }}
                >
                  <CircularProgress size="sm" />
                  <Typography level="body-xs" color="neutral">
                    {effectiveStatus === 'queued' ? 'Queued…' : 'Extracting…'}
                  </Typography>
                </Box>
              )}
            </Box>

            {caption && (
              <Typography level="body-xs" color="neutral">
                {caption}
              </Typography>
            )}
          </Stack>
        </CardContent>
      </Card>
    </Stack>
  );
};

export default JobDescriptionInput;
